import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { AnalyticsService, ExportJob } from '@pulse-front/core';

@Component({
  selector: 'panel-b2b-export-list',
  templateUrl: './export-list.component.html',
  styleUrls: ['./export-list.component.scss'],
})
export class ExportListComponent implements OnInit, OnDestroy {
  jobs: ExportJob[] = [];
  isAdmin = false;
  isLoading = false;
  downloadingJobUuid: string | null = null;

  private pollSubscription: Subscription | null = null;
  private readonly POLL_INTERVAL_MS = 5000;
  private readonly ACTIVE_STATUSES: ExportJob['jobStatus'][] = ['PENDING', 'PROCESSING'];

  constructor(
    private analyticsService: AnalyticsService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.router.url === '/exports/admin';
    this.loadJobs();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  loadJobs(): void {
    this.isLoading = true;
    const request$ = this.isAdmin
      ? this.analyticsService.getAllExportJobs()
      : this.analyticsService.getMyExportJobs();

    request$.subscribe({
      next: (res) => {
        this.jobs = res?.data?.jobs ?? [];
        this.isLoading = false;
        this.restartPollingIfNeeded();
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Error al cargar los reportes', 'Cerrar', { duration: 3000 });
      }
    });
  }

  hasActiveJobs(): boolean {
    return this.jobs.some(j => this.ACTIVE_STATUSES.includes(j.jobStatus));
  }

  private startPolling(): void {
    if (this.pollSubscription) return;
    this.pollSubscription = interval(this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.isAdmin
        ? this.analyticsService.getAllExportJobs()
        : this.analyticsService.getMyExportJobs()
      ),
      takeWhile(() => this.hasActiveJobs(), true)
    ).subscribe({
      next: (res) => {
        this.jobs = res?.data?.jobs ?? [];
        if (!this.hasActiveJobs()) this.stopPolling();
      },
      error: () => this.stopPolling()
    });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  private restartPollingIfNeeded(): void {
    if (this.hasActiveJobs() && !this.pollSubscription) this.startPolling();
    else if (!this.hasActiveJobs()) this.stopPolling();
  }

  onRefresh(): void { this.loadJobs(); }

  downloadJob(job: ExportJob): void {
    if (job.jobStatus !== 'COMPLETED') return;
    this.downloadingJobUuid = job.jobUuid;
    this.analyticsService.downloadExportJob(job.jobUuid).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporte_${job.jobType}_${job.jobUuid.slice(0, 8)}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloadingJobUuid = null;
      },
      error: () => {
        this.snackBar.open('Error al descargar el reporte', 'Cerrar', { duration: 3000 });
        this.downloadingJobUuid = null;
      }
    });
  }

  getStatusClass(status: ExportJob['jobStatus']): string {
    const map = { PENDING: 'badge-pending', PROCESSING: 'badge-processing', COMPLETED: 'badge-completed', FAILED: 'badge-failed' };
    return map[status] ?? '';
  }

  getStatusLabel(status: ExportJob['jobStatus']): string {
    const map = { PENDING: 'Pendiente', PROCESSING: 'Procesando', COMPLETED: 'Completado', FAILED: 'Fallido' };
    return map[status] ?? status;
  }
}
